/**
 * New API client (UI-HONEST: the page's "New API client" button had no
 * handler). `POST /v1/api-clients` with a name and the permission codes the
 * client may use; the API answers with the client's id and its secret, which
 * it never shows again (`kernel/identity/api-clients.controller.ts`; the
 * client then signs in with `POST /v1/oauth/token`, client credentials).
 *
 * What may be given is the API's decision: only someone with workspace-wide
 * scope may create a client (403 SCOPE_DENIED), and only codes the creator
 * holds (403 PERMISSION_DENIED, naming the field). Either refusal is shown in
 * the person's language, never as success.
 *
 * The permission list is searched on the server (`GET /v1/permissions?q=`),
 * which returns at most 200 codes; when a search fills that, the screen says
 * so rather than presenting a partial list as the whole.
 */
import { useId, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Card, Field, Input } from "@/components/ui";
import { ErrorText } from "@/components/ErrorText";
import { post, tenantKey, useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { ltr } from "@/lib/i18n/format";

interface PermissionRow {
  id: string;
  code: string;
  module: string;
}

/**
 * `POST /v1/api-clients`'s answer: the row, its secret once. A POST's body is
 * passed on as the API wrote it (lib/api/client.ts adds camelCase names only
 * to reads), so these are the column names.
 */
interface CreatedClient {
  client_id: string;
  client_secret: string;
}

/** The API's cap on one permission search (permissions.controller.ts). */
const SEARCH_CAP = 200;

export function NewApiClient({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const scopesId = useId();
  const [name, setName] = useState("");
  const [query, setQuery] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<CreatedClient | null>(null);
  const [copied, setCopied] = useState(false);

  const q = query.trim();
  const permissions = useApiList<PermissionRow>(
    ["permissions", "search", q],
    q ? `/permissions?q=${encodeURIComponent(q)}` : "/permissions",
  );
  const found = permissions.data?.items ?? [];

  const toggle = (code: string) =>
    setChosen((now) =>
      now.includes(code) ? now.filter((c) => c !== code) : [...now, code],
    );

  const submit = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    post<CreatedClient>("/api-clients", { name: name.trim(), scopes: chosen })
      .then((client) => {
        setCreated(client);
        void queryClient.invalidateQueries({
          queryKey: tenantKey(["admin", "api-clients"]),
        });
      })
      .catch((err: unknown) => setError(err))
      .finally(() => setBusy(false));
  };

  if (created) {
    return (
      <Card title={t("apiClientCreate.createdTitle")} className="mb-4">
        <p role="status" style={{ marginBlockStart: 0 }}>
          {t("apiClientCreate.createdBody")}
        </p>
        <dl>
          <dt className="atlas-label">{t("admin.apiWebhooks.clientId")}</dt>
          <dd className="atlas-mono">
            <bdi dir="ltr">{created.client_id}</bdi>
          </dd>
          <dt className="atlas-label">{t("apiClientCreate.secret")}</dt>
          <dd className="atlas-mono" style={{ wordBreak: "break-all" }}>
            <bdi dir="ltr">{created.client_secret}</bdi>
          </dd>
        </dl>
        <div style={{ display: "flex", gap: 8 }}>
          {/* Offered only where the browser can write the clipboard (a
              secure page); elsewhere the secret is selected and copied by
              hand. */}
          {typeof navigator !== "undefined" && navigator.clipboard && (
            <Button
              onClick={() => {
                void navigator.clipboard
                  .writeText(created.client_secret)
                  .then(() => setCopied(true));
              }}
            >
              {copied ? t("apiClientCreate.copied") : t("apiClientCreate.copy")}
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            {t("apiClientCreate.done")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title={t("apiClientCreate.title")} className="mb-4">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field label={t("fields.name")} required>
          <Input
            value={name}
            required
            maxLength={200}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <fieldset
          aria-describedby={`${scopesId}hint`}
          style={{ border: 0, padding: 0, margin: 0 }}
        >
          <legend className="atlas-label">{t("apiClientCreate.scopes")}</legend>
          <p
            id={`${scopesId}hint`}
            style={{ fontSize: 12, marginBlock: "0 8px" }}
          >
            {t("apiClientCreate.scopesHint")}
          </p>
          <Field label={t("apiClientCreate.filter")}>
            <Input
              type="search"
              value={query}
              dir="ltr"
              onChange={(e) => setQuery(e.target.value)}
            />
          </Field>
          <div
            style={{
              maxBlockSize: 220,
              overflowY: "auto",
              border: "1px solid var(--atlas-border)",
              borderRadius: "var(--atlas-radius-sm)",
              padding: 8,
            }}
          >
            {permissions.error ? (
              <div role="alert" style={{ color: "var(--atlas-danger)" }}>
                <ErrorText error={permissions.error} />
              </div>
            ) : (
              found.map((p) => (
                <label
                  key={p.id}
                  style={{ display: "flex", gap: 8, alignItems: "center" }}
                >
                  <input
                    type="checkbox"
                    checked={chosen.includes(p.code)}
                    onChange={() => toggle(p.code)}
                  />
                  <span className="atlas-mono">{ltr(p.code)}</span>
                </label>
              ))
            )}
          </div>
          {found.length >= SEARCH_CAP && (
            <p style={{ fontSize: 12, marginBlock: "4px 0" }}>
              {t("apiClientCreate.narrow", { limit: SEARCH_CAP })}
            </p>
          )}
          <p role="status" style={{ fontSize: 13, marginBlock: "8px 0" }}>
            {t("apiClientCreate.chosen", { count: chosen.length })}
          </p>
        </fieldset>
        {error !== null && (
          <div
            role="alert"
            style={{
              color: "var(--atlas-danger)",
              fontSize: 13,
              marginBlock: 8,
            }}
          >
            <ErrorText error={error} />
          </div>
        )}
        <div style={{ display: "flex", gap: 8, marginBlockStart: 12 }}>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? t("apiClientCreate.creating") : t("apiClientCreate.create")}
          </Button>
          <Button disabled={busy} onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </Card>
  );
}
